import React, { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from './auth.module.scss'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText'
import validator from 'validator'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Role } from '@/types/Role'
import { FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { RECAPTCHA, SIGN_UP } from '@/modules/API/account/accountHandler'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import Loading from '@/components/Loading/Loading'
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useAuth } from '@/modules/context/AuthContext'

interface SignUpProps {
    email: string
    setEmail: (email: string) => void
    returnToLogin: () => void
    deviceType?: string
    autoLink?: string
    roleRequested?: string
    resetSelectedRole: () => void
    setJustSignedUp: (value: boolean) => void
    logo?: string
}

const Signup: FunctionComponent<SignUpProps> = ({
    returnToLogin,
    email,
    setEmail,
    deviceType,
    autoLink,
    roleRequested,
    resetSelectedRole,
    setJustSignedUp,
    logo
}: SignUpProps) => {
    const { t } = useTranslation()

    const { login, pushToken } = useAuth()

    const [emailValid, setEmailValid] = useState<boolean>(validator.isEmail(email) && email !== '')

    const [password, setPassword] = useState<string>('')
    const [passwordValid, setPasswordValid] = useState<boolean>(false)

    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [confirmPasswordValid, setConfirmPasswordValid] = useState<boolean>(false)

    const [selectRoleView, setSelectRoleView] = useState<boolean>(false)
    const [role, setRole] = useState<string>(roleRequested ? roleRequested : 'parent')

    const [errorMessage, setErrorMessage] = useState<string>('')

    function roleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        setRole(e.target.value as Role)
    }

    const [token, setToken] = useState<string>('')
    const [refreshReCaptcha, setRefreshReCaptcha] = useState<boolean>(false)

    const [verifyCaptcha, { data: dataRecaptcha, error: errorRecaptcha }] = useLazyQuery(RECAPTCHA, {
        variables: {
            response: token
        }
    })

    useEffect(() => {
        if (dataRecaptcha) {
            setRefreshReCaptcha(true)
            setSelectRoleView(true)
        }
    }, [dataRecaptcha])

    useEffect(() => {
        if (errorRecaptcha) {
            setRefreshReCaptcha(false)
        }
    }, [errorRecaptcha])

    const roleList: { value: string; label: string }[] = [
        { value: 'parent', label: t('oneParent') },
        { value: 'staff', label: t('daycarePro') },
        { value: 'assmat', label: t('nanny') }
    ]

    const [signUp, { loading: loadingSignUp }] = useMutation(SIGN_UP, {
        onCompleted: ({ signUp }): void => {
            setJustSignedUp(true)
            login(signUp.user, signUp.token)
            setPassword('')
        },
        onError: () => {
            setErrorMessage(t('generalError'))
        }
    })

    async function handlerSignUp(): Promise<void> {
        if (role === 'parent' || role === 'assmat') {
            await signUp({
                variables: {
                    email: email,
                    password: password,
                    pptosAcceptation: true,
                    pushToken: pushToken,
                    deviceType: deviceType,
                    role: 'parent',
                    isNanny: role === 'assmat',
                    autoLink: autoLink
                }
            })
        } else if (role === 'staff') {
            await signUp({
                variables: {
                    email: email,
                    password: password,
                    pptosAcceptation: true,
                    pushToken: pushToken,
                    deviceType: deviceType,
                    role: 'staff',
                    isNanny: false,
                    autoLink: autoLink
                }
            })
        }
    }

    function onKeyPress(e: React.KeyboardEvent<HTMLInputElement>): void {
        if (e.key === 'Enter' && emailValid && passwordValid && confirmPasswordValid && password === confirmPassword) {
            setSelectRoleView(true)
        }
    }

    return (
        <div className={styles.Form}>
            {loadingSignUp && <Loading overall />}
            {!selectRoleView ? (
                <>
                    <div className={styles.Title}>
                        <Title text={t('signUp')} />
                    </div>
                    <div className={styles.Input}>
                        <UserInput
                            id='email'
                            label={t('email')}
                            type={'text'}
                            verify={'email'}
                            unallowedCharacters={'email'}
                            required
                            fullWidth
                            value={email}
                            setValue={setEmail}
                            setValid={setEmailValid}
                        />
                        <UserInput
                            id='password'
                            label={t('password')}
                            type={'password'}
                            verify={'password'}
                            required
                            fullWidth
                            className={styles.InputField}
                            value={password}
                            setValue={setPassword}
                            setValid={setPasswordValid}
                            onKeyPress={onKeyPress}
                        />
                        <UserInput
                            id='confirmPassword'
                            label={t('confirmPassword')}
                            type={'password'}
                            verify={'confirmPassword'}
                            confirmPasswordValue={password}
                            required
                            fullWidth
                            className={styles.InputField}
                            value={confirmPassword}
                            setValue={setConfirmPassword}
                            setValid={setConfirmPasswordValid}
                            onKeyPress={onKeyPress}
                        />
                    </div>
                    <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''}>
                        <GoogleReCaptcha onVerify={setToken} refreshReCaptcha={refreshReCaptcha} />
                    </GoogleReCaptchaProvider>
                    <ButtonText
                        text={t('continue')}
                        onClick={verifyCaptcha}
                        fullWidth
                        disabled={
                            !emailValid || !passwordValid || !confirmPasswordValid || password !== confirmPassword
                        }
                    />
                    <a className={styles.Link} onClick={returnToLogin}>
                        {t('returnToLogin')}
                    </a>
                </>
            ) : (
                <>
                    <div className={styles.Title}>
                        <Title text={t('iAm')} />
                    </div>
                    <RadioGroup name='role' value={role} onChange={roleChange} className={styles.Card}>
                        {roleRequested ? (
                            <>
                                {roleList
                                    .filter(
                                        (item: { value: string; label: string }): boolean =>
                                            item.value === roleRequested
                                    )
                                    .map(
                                        (item: { value: string; label: string }, index: number): JSX.Element => (
                                            <FormControlLabel
                                                key={index}
                                                value={item.value}
                                                control={<Radio />}
                                                label={item.label}
                                                className={styles.selectCard}
                                            />
                                        )
                                    )}
                                <a className={styles.LinkStatut} onClick={resetSelectedRole}>
                                    {t('toChangeStatut')} <span className={styles.Span}>{t('clickHere2')}</span>
                                </a>
                            </>
                        ) : (
                            roleList.map((item: { value: string; label: string }, index: number) => (
                                <FormControlLabel
                                    key={index}
                                    value={item.value}
                                    control={<Radio />}
                                    label={item.label}
                                    className={styles.selectCard}
                                />
                            ))
                        )}
                    </RadioGroup>
                    <p className={styles.Text}>
                        {t('byClickingSignUpYouAgreeToThe')}
                        <a
                            className={styles.Link}
                            href='https://www.cald.fr/conditions-generales-dutilisation/'
                            rel='noreferrer'
                            target='_blank'
                        >
                            {' '}
                            {t('termsAndConditions')}
                        </a>
                        {t('signUpPart1')}
                        <a
                            className={styles.Link}
                            href='https://www.cald.fr/politique-de-confidentialite/'
                            rel='noreferrer'
                            target='_blank'
                        >
                            {' '}
                            {t('privacyPolicy')}
                        </a>
                        {t('signUpPart2')}
                        <a
                            className={styles.Link}
                            href='https://www.cald.fr/politique-cookies/'
                            rel='noreferrer'
                            target='_blank'
                        >
                            {' '}
                            {t('cookiePolicy')}
                        </a>
                        {t('signUpPart3')}
                    </p>
                    <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''}>
                        <ButtonText
                            text={t('iSignUp')}
                            onClick={handlerSignUp}
                            fullWidth
                            disabled={
                                !emailValid || !passwordValid || !confirmPasswordValid || password !== confirmPassword
                            }
                        />
                        <a className={styles.Link} onClick={() => setSelectRoleView(false)}>
                            {t('return')}
                        </a>
                    </GoogleReCaptchaProvider>
                    {errorMessage && <ErrorMessage error={errorMessage} className={styles.ErrorPosition} />}
                </>
            )}
            {logo && <img src={`data:image/png;base64,${logo}`} alt={'logo'} className={styles.Logo} />}
        </div>
    )
}

export default Signup
